import { useForm } from "react-hook-form";
import { House, Mail } from "lucide-react";
import * as accountApi from "../../../api/account/api";
import { BackToLoginLink } from "../components/BackToLoginLink";
import { ErrorAlert } from "../components/ErrorAlert";
import { SubmitButton } from "../components/SubmitButton";
import { LoginFormField } from "../components/LoginFormField";
import { useApiError } from "../hooks/useApiError";
import { LoginViewState } from "../types";
import { ViewHeading } from "../components/ViewHeading";
import { FormOverlay } from "@/components/common/Loading";

type Props = {
  initialAccount?: string;
  onViewChange: (view: LoginViewState) => void;
};

type ForgotFormValues = {
  account: string;
  email: string;
};

export const ForgotPasswordView = ({ initialAccount = "", onViewChange }: Props) => {
  const { apiError, setApiError, clearError } = useApiError();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<ForgotFormValues>({
    defaultValues: { account: initialAccount, email: "" },
  });

  const onSubmit = async (data: ForgotFormValues) => {
    clearError();
    try {
      await accountApi.forgotPassword({ account: data.account.trim(), email: data.email.trim() });
      onViewChange("forgotPasswordSent");
    } catch {
      setApiError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <>
      <FormOverlay loading={isSubmitting}>

      <ViewHeading
        title="Forgotten Klipboard CRM Password"
        subtitle="Enter your account and email. If the email is valid for the account we will send a link to
        reset your password."
      />

      {apiError && <ErrorAlert message={apiError} />}

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <LoginFormField
          label="Account"
          placeholder="Account"
          autoComplete="organization"
          error={errors.account?.message}
          disabled={isSubmitting}
          icon={<House size={16} color="#9CA3AF" strokeWidth={2} />}
          {...register("account", { required: "Account is required" })}
        />

        <LoginFormField
          label="Email"
          type="email"
          placeholder="Email"
          autoComplete="email"
          error={errors.email?.message}
          disabled={isSubmitting}
          icon={<Mail size={16} color="#9CA3AF" strokeWidth={2} />}
          {...register("email", {
            required: "Email is required",
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email address" },
          })}
        />

        <div className="mb-3">
          <SubmitButton loading={isSubmitting}>Email Link</SubmitButton>
        </div>
      </form>

      <BackToLoginLink onClick={() => onViewChange("login")} />
      </FormOverlay>
    </>
  );
};
